import { ButtonTheme, FormLabelStyle, IFormFieldData, IMembersAreaWidgetData } from "@pageBuilder/typings";
import { WidgetRendererBase } from "../../widget-renderer-base";
import { Base64Util } from "src/utils/base64-util";
import { FormStyleRenderer } from "../../form-widget/form-style-renderer";
import { MembersAreaWidgetContainerStyleRenderer } from "./members-area-widget-container-style-renderer";
import { BrowserIdentifierUtil, IRenderingCommonContext } from "@commonRendering/types";

export abstract class MembersAreaWidgetRendererBase<TMembersAreaWidgetData extends IMembersAreaWidgetData> extends WidgetRendererBase<TMembersAreaWidgetData> {

    protected readonly EMAIL_MAX_LENGTH = 254;
    protected readonly PASSWORD_MAX_LENGTH = 128;

    protected abstract DATA_ATTRIBUTE: string;

    protected readonly styleRenderer: FormStyleRenderer;
    protected readonly containerStyleRenderer: MembersAreaWidgetContainerStyleRenderer;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.styleRenderer = new FormStyleRenderer();
        this.containerStyleRenderer = new MembersAreaWidgetContainerStyleRenderer();
    }

    protected setAttributes(element: HTMLElement, data: TMembersAreaWidgetData): void {
        const dataForSave = this.prepareDataForSave(data);
        const json = Base64Util.serialize(dataForSave) || "";

        element.setAttribute(this.DATA_ATTRIBUTE, json);
        element.toggleAttribute("data-primary-element", data.primaryElement);
        if (this.isEditorRendering) {
            element.setAttribute("data-draft", "true");
        }
    }

    protected ensureFormElement(content: HTMLElement): HTMLFormElement {
        let form = content.querySelector("form");
        if (!form) {
            form = document.createElement("form");
            form.classList.add("widget-form");
            content.appendChild(form);
        }

        return form;
    }

    protected createFormField(): HTMLDivElement {
        const field = document.createElement("div");
        field.classList.add("field");
        return field;
    }

    protected createFormInput(): HTMLInputElement {
        const input = document.createElement("input");
        input.classList.add("control");
        return input;
    }

    protected renderControl(container: HTMLElement, fieldData: IFormFieldData | undefined, params: IRenderMAControlParams): void {
        if (!fieldData) {
            return;
        }

        const field = this.createFormField();

        if (params.labelStyle === "standard") {
            const label = document.createElement("label");
            label.classList.add("field-label");
            label.innerText = params.labelText;
            label.htmlFor = fieldData.id;

            field.append(label);
        }

        const inputWrapper = document.createElement("div");
        inputWrapper.classList.add("input-wrapper");

        const input = this.createFormInput();
        input.type = params.inputType;
        input.id = fieldData.id;
        input.maxLength = params.inputMaxLength;
        input.name = params.inputName;

        input.setAttribute("autocomplete", params.autocomplete);

        if (params.labelStyle === "inline") {
            input.placeholder = fieldData.caption;
        }

        inputWrapper.appendChild(input);
        field.append(inputWrapper);

        container.appendChild(field);
    }

    protected renderSubmitButton(container: HTMLElement, theme: ButtonTheme, submitText: string): void {
        let actions = container.querySelector<HTMLElement>(".actions");
        if (!actions) {
            actions = document.createElement("div");
            actions.classList.add("actions");
            container.appendChild(actions);
        }

        const button = actions.querySelector(".submit");
        if (!button) {
            const button = document.createElement("button");
            button.type = "submit";

            button.classList.toggle("secondary", theme === "secondary");

            button.innerText = submitText;
            button.classList.add("submit");

            actions.append(button);
        }
    }

    protected override renderStyles(element: HTMLElement, data: TMembersAreaWidgetData): void {
        const animationStyles = this.animationRenderer.getAnimationStyles(data.style.animation) ?? [];
        this.animationRenderer.setAnimationStyles(element, data.style.animation);

        const containerStyles = this.containerStyleRenderer.getStyles(data.containerStyle);
        const styleProperties = this.styleRenderer.getStyles(data.style);

        element.classList.remove("text-left", "text-center", "text-right");

        if (data.containerStyle.textAlign) {
            if (data.containerStyle.textAlign === "start") {
                element.classList.add("text-left");
            } else if (data.containerStyle.textAlign === "center") {
                element.classList.add("text-center");
            } else if (data.containerStyle.textAlign === "end") {
                element.classList.add("text-right");
            }
        }

        this.styleService.setStyle(element, `#${element.id}`, [...containerStyles, ...styleProperties, ...animationStyles]);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected override cleanupInternal(element: HTMLElement): void {
        return;
    }

    protected abstract prepareDataForSave(data: TMembersAreaWidgetData): unknown;

    protected abstract override createNewInternal(): HTMLElement;

    protected abstract override renderInternal(element: HTMLElement, data: TMembersAreaWidgetData): HTMLElement;
    protected abstract override getContentSelectorsInternal(element: HTMLElement): Array<string>;

}

export interface IRenderMAControlParams {
    inputName: string;
    inputType: string;
    inputMaxLength: number;
    autocomplete: string;
    labelText: string;
    labelStyle: FormLabelStyle;
    withSafariAutofill?: boolean;
}